import React, { useEffect, useRef, useState } from 'react';

import { gsap } from 'gsap';
import Lottie, { type LottieRef } from 'lottie-react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ShoppingCartIcon } from '@pkg/assets/icons/v2/shopping-cart.svg';
import { CircleButton, YatMagnetism } from '@pkg/components';
import { Badge } from '@pkg/components/Badge/Badge';

import data from '../../../lotties/add-to-cart-confetti/confetti.json';

import './CartButton.scss';

interface CartButtonProps {
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    cartItems?: number;
}

const CartButton = ({ onClick, cartItems = 0 }: CartButtonProps) => {
    const { t } = useTranslation(['yat_picker']);
    const [cartItemsAmount, setCartItemsAmount] = useState(cartItems);
    const tl = useRef<GSAPTimeline>();
    const buttonRef = useRef() as React.MutableRefObject<HTMLDivElement>;
    const lottieRef = useRef() as LottieRef;

    const animationData = data;
    const rendererSettings = {
        preserveAspectRatio: 'xMidYMid slice',
    };

    useEffect(() => {
        if (tl.current && cartItems > cartItemsAmount) {
            tl.current.play(0);
        } else {
            setCartItemsAmount(cartItems);
        }
    }, [cartItems, cartItemsAmount]);

    useEffect(() => {
        if (!lottieRef.current) {
            return;
        }

        tl.current = gsap.timeline({ paused: true });
        const button = buttonRef.current;
        setCartItemsAmount(cartItems);

        tl.current
            .call(lottieRef.current.play)
            .to(button, { scale: 0.75, duration: 0.35 })
            .to(button, { scale: 1.15, duration: 0.1, delay: 0.1 })
            .to(button, { scale: 1, duration: 1.25, ease: 'elastic.out(1, 0.3)', delay: 0.25 })
            .set(button, { clearProps: 'all' })
            .call(lottieRef.current.stop);

        return () => {
            if (tl.current) {
                tl.current.kill();
            }
        };
    }, [tl, buttonRef, lottieRef, cartItems]);

    return (
        <YatMagnetism classes={`yat-picker-cart-button${cartItems === 0 ? ' yat-picker-cart-button--is-hidden' : ''}`}>
            <div className="yat-picker-cart-button__confetti">
                <Lottie
                    loop={false}
                    autoplay={false}
                    animationData={animationData}
                    rendererSettings={rendererSettings}
                    lottieRef={lottieRef}
                />
            </div>
            <div ref={buttonRef} className="yat-picker-cart-button__button">
                <CircleButton onClick={onClick} ariaLabel={t('open_cart')} size="large">
                    <ShoppingCartIcon className="yat-picker-cart-button__icon" />
                </CircleButton>
                <Badge classes="yat-picker-cart-button__badge">{cartItems}</Badge>
            </div>
        </YatMagnetism>
    );
};

export default CartButton;
